import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Search } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fieldCls = 'px-3 py-2 rounded-lg border border-[#E5E5E5] focus:ring-2 focus:ring-[#A0451E]/50 bg-white font-body text-sm';

export const CommissionTable = () => {
  const [rows, setRows] = useState(null);
  const [query, setQuery] = useState('');
  const [house, setHouse] = useState('');

  useEffect(() => {
    axios.get(`${API}/commissions`).then((res) => setRows(res.data)).catch(() => setRows([]));
  }, []);

  const houses = useMemo(() => [...new Set((rows || []).map((r) => r.fund_house))], [rows]);
  const q = query.trim().toLowerCase();
  const filtered = (rows || []).filter(
    (r) => (!house || r.fund_house === house) && (!q || `${r.fund_house} ${r.scheme} ${r.category}`.toLowerCase().includes(q))
  );
  const lastUpdated = (rows || []).reduce((m, r) => (r.updated_at > m ? r.updated_at : m), '');

  if (rows === null) return null;
  if (rows.length === 0) {
    return (
      <p data-testid="commission-rates-placeholder">
        Scheme-wise commission rates received or receivable for competing schemes will be published here shortly. Please contact us at
        cs@impetusinvestments.in for details.
      </p>
    );
  }

  return (
    <div className="space-y-3" data-testid="commission-table-section">
      <p className="font-semibold text-[#A0451E]">Scheme-wise trail commission rates received or receivable</p>
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-grow">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#737373]" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search scheme or fund house" className={`${fieldCls} w-full pl-9`} data-testid="commission-search-input" />
        </div>
        <select value={house} onChange={(e) => setHouse(e.target.value)} className={fieldCls} data-testid="commission-fund-house-filter">
          <option value="">All fund houses</option>
          {houses.map((h) => <option key={h}>{h}</option>)}
        </select>
      </div>
      <p className="text-xs text-[#737373] sm:hidden" data-testid="commission-scroll-hint">Swipe the table sideways to see all columns.</p>
      <div className="bg-white rounded-xl border border-[#E5E5E5] overflow-x-auto max-h-[480px] overflow-y-auto">
        <table className="w-full min-w-[560px] text-sm" data-testid="commission-public-table">
          <thead className="bg-[#FDF8F5] text-left text-[#A0451E] sticky top-0">
            <tr><th className="p-3">Fund House</th><th className="p-3">Scheme</th><th className="p-3">Category</th><th className="p-3 whitespace-nowrap">Trail % p.a.</th></tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <tr key={r.commission_id} className="border-t border-[#E5E5E5]" data-testid={`commission-row-${r.commission_id}`}>
                <td className="p-3">{r.fund_house}</td>
                <td className="p-3">{r.scheme}</td>
                <td className="p-3">{r.category}</td>
                <td className="p-3">{r.trail_percent.toFixed(2)}%</td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={4} className="p-6 text-center text-[#737373]" data-testid="commission-no-results">No schemes match your search.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-[#737373]" data-testid="commission-last-updated">
        Rates are for Regular Plans, may change from time to time and are as last updated on{' '}
        {new Date(lastUpdated).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}.
      </p>
    </div>
  );
};
